import React, { useState } from "react";
import { MessageSquare, FileText, ClipboardList, HelpCircle, ArrowDown, Check, ChevronDown } from "lucide-react";
import { newItems, ack, useSignalsVersion } from "./thSignals";

// "Just in" — what the patient sent since you last acknowledged it, newest first, at the top of the visit.
// Intake answers fold into one group; only the newest item is open, the rest are one-line rows.
const AMBER = "#B26A00", FAINT = "#9B9B97", GREY2 = "#6C6C69";
const ICON = { answer: ClipboardList, question: HelpCircle, document: FileText, message: MessageSquare };
const TARGET = { answer: () => "th-intake-panel", question: () => "th-card-from", document: (x) => `th-docx-${x.docId}`, message: () => "th-msg-panel" };
const time = (t) => (t ? new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

function docLine(x, docs) {
  const d = docs.find((y) => y.doc_id === x.docId), ex = d?.extraction;
  if (!d) return null;
  const abn = (ex?.lab_results || []).filter((r) => r.flag && !/normal/i.test(r.flag)).map((r) => `${r.test_name} ${r.value}${r.unit ? ` ${r.unit}` : ""} ${r.flag}`);
  return [d.document_type && d.document_type.replace(/_/g, " "), d.status !== "completed" ? "analysis running" : null, abn.length ? `${abn.length} flagged: ${abn.join(" · ")}` : ex ? "nothing flagged" : null].filter(Boolean).join(" · ");
}

function groupItems(items) {
  const out = []; let ans = null;
  for (const x of items) {
    if (x.kind !== "answer") { out.push(x); continue; }
    if (!ans) { ans = { key: "grp:answers", kind: "answer", title: "Intake answers", at: x.at, items: [] }; out.push(ans); }
    ans.items.push(x);
    if (x.at && (!ans.at || x.at > ans.at)) ans.at = x.at;
  }
  return out;
}

function Row({ g, open, onToggle, docs, onJump, onOk }) {
  const Ic = ICON[g.kind], grp = !!g.items, extra = g.kind === "document" ? docLine(g, docs) : null;
  const body = g.kind === "question" ? `"${g.body}"` : g.body;
  const summary = grp ? g.items.map((x) => x.body).join(" · ") : [body, extra].filter(Boolean).join(" · ");
  return (
    <div className="th-ji-row py-1.5 border-t first:border-t-0" style={{ borderColor: "#F1E4CC" }} data-testid={`th-justin-item-${grp ? "answers" : g.kind}`}>
      <div className="flex items-start gap-2.5">
        <Ic size={14} className="mt-0.5 flex-none" style={{ color: AMBER }} />
        <button type="button" className="min-w-0 flex-1 text-left text-[12.5px]" onClick={onToggle} aria-expanded={open} data-testid={`th-justin-toggle-${g.key}`}>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-semibold whitespace-nowrap">{g.title}</span>
            {grp ? <span className="text-[11px] font-bold px-1.5 rounded-full" style={{ background: "#F6E3C2", color: AMBER }}>{g.items.length}</span> : null}
            <span className="whitespace-nowrap" style={{ color: FAINT }}>· {time(g.at)}</span>
            <ChevronDown size={13} className="flex-none" style={{ color: FAINT, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
            {!open ? <span className="truncate min-w-0" style={{ color: GREY2 }}>{summary}</span> : null}
          </div>
          {open && !grp ? (
            <>
              {g.q ? <div style={{ color: GREY2 }}>{g.q}</div> : null}
              <div className="font-medium break-words">{body}</div>
              {extra ? <div style={{ color: AMBER }}>{extra}</div> : null}
            </>
          ) : null}
        </button>
        <button type="button" className="text-[12px] font-semibold inline-flex items-center gap-1 whitespace-nowrap" style={{ color: "#4A4AC2" }} onClick={onJump} data-testid={`th-justin-jump-${g.key}`}><ArrowDown size={12} />Jump to</button>
        <button type="button" className="text-[12px] font-semibold inline-flex items-center gap-1 whitespace-nowrap" onClick={onOk} data-testid={`th-justin-ok-${g.key}`}><Check size={12} />Got it</button>
      </div>
      {open && grp ? (
        <div className="ml-[24px] mt-1.5 flex flex-col gap-1.5 text-[12.5px]" data-testid="th-justin-answers">
          {g.items.map((x) => <div key={x.key}><div style={{ color: GREY2 }}>{x.q}</div><div className="font-medium break-words">{x.body}</div></div>)}
        </div>
      ) : null}
    </div>
  );
}

export function JustIn({ a, docs = [] }) {
  useSignalsVersion();
  const [openKey, setOpenKey] = useState(undefined);
  const items = newItems(a), id = a.appointment_id;
  if (!items.length) return null;
  const groups = groupItems(items);
  const keysOf = (g) => (g.items ? g.items.map((x) => x.key) : g.key);
  // Newest item starts open; the intake group opens only on click.
  const isOpen = (g, i) => (openKey === undefined ? i === 0 && !g.items : openKey === g.key);
  const jump = (g) => {
    ack(id, keysOf(g));
    const el = document.querySelector(`[data-testid="${TARGET[g.kind](g)}"]`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("th-flash"); setTimeout(() => el.classList.remove("th-flash"), 1600); }
  };
  return (
    <div className="mt-3 rounded-[12px] p-3" style={{ background: "#FFF8EC", boxShadow: `inset 3px 0 0 ${AMBER}` }} data-testid={`th-justin-${id}`}>
      <style>{`.th-flash{animation:thFlash 1.6s cubic-bezier(.32,.72,0,1)}@keyframes thFlash{0%,40%{box-shadow:0 0 0 2px ${AMBER}}100%{box-shadow:0 0 0 0 transparent}}.th-ji-row{animation:thJi .44s cubic-bezier(.32,.72,0,1) both}@keyframes thJi{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}@media (prefers-reduced-motion:reduce){.th-flash,.th-ji-row{animation:none}}`}</style>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="th-dot sm pulse" style={{ background: AMBER }} />
        <span className="text-[13px] font-semibold">Just in</span>
        <span className="text-[11.5px] font-bold px-2 py-[1px] rounded-full text-white" style={{ background: AMBER }} data-testid={`th-justin-count-${id}`}>{items.length}</span>
        <span className="text-[12px]" style={{ color: GREY2 }}>new from the patient</span>
        <button type="button" className="ml-auto text-[12px] font-semibold underline" onClick={() => ack(id, items.map((x) => x.key))} data-testid={`th-justin-all-${id}`}>Mark all seen</button>
      </div>
      {groups.map((g, i) => {
        const open = isOpen(g, i);
        return <Row key={g.key} g={g} open={open} docs={docs} onToggle={() => setOpenKey(open ? null : g.key)} onJump={() => jump(g)} onOk={() => ack(id, keysOf(g))} />;
      })}
    </div>
  );
}
